import { z } from 'zod';
import type { components } from './schema.js';
import { montarCaminho, requisitar } from './client.js';
import { erroDeContrato } from './erros.js';
import type { Pagina, ParametrosDePagina } from './paginacao.js';
import { paginaSchema, parametrosDePagina } from './paginacao.js';
import { qrDoProdutoSchema } from './produtos.js';
import { analisar } from './validacao.js';

/**
 * Lotes sincronizados do ERP, o QR de lote e a carga de lotes, com os zod
 * conferidos contra o OpenAPI gerado.
 */

type Esquemas = components['schemas'];

const CAMINHO_LOTES = montarCaminho('/lotes');
const CAMINHO_QR = montarCaminho('/lotes/qrcode');
const CAMINHO_CARGA = montarCaminho('/sincronizacao/lotes/carga');

export const loteSchema = z.object({
  codFil: z.number().int(),
  codDep: z.string(),
  codPro: z.string(),
  codDer: z.string(),
  codLot: z.string(),
  /** `AAAA-MM-DD` ou ISO; `null` quando o lote não tem validade. */
  datVlt: z.string().nullable(),
  /** Texto decimal, como toda quantidade da API. */
  quantidadeEstoque: z.string(),
  descricaoProduto: z.string().nullable(),
  /**
   * Ativo e não excluído (a régua do filtro `situacao`). Opcional até o
   * middleware publicado mandar — regenere o schema e torne obrigatório.
   */
  ativo: z.boolean().optional(),
  /** Última leitura do ERP. Opcional pelo mesmo motivo. */
  sincronizadoEm: z.string().optional(),
}) satisfies z.ZodType<Esquemas['LoteDoPainelResponse']>;

/** Um lote em um depósito: o mesmo `codLot` aparece uma vez por depósito com saldo. */
export type Lote = z.infer<typeof loteSchema>;

const paginaDeLotesSchema = paginaSchema(loteSchema);

/** `ativos` é o padrão da API; `foraDeUso` são os inativados por uma varredura. */
export const SITUACOES_DO_LOTE = ['ativos', 'foraDeUso', 'todos'] as const;

export type SituacaoDoLote = (typeof SITUACOES_DO_LOTE)[number];

export type FiltroDeLotes = ParametrosDePagina & {
  /** Trecho do `codPro` ou do `codLot`. */
  readonly busca?: string;
  readonly codFil?: number;
  /** Só vai quando informada: middleware sem o filtro recusaria o parâmetro. */
  readonly situacao?: SituacaoDoLote;
};

export async function buscarLotes(filtro: FiltroDeLotes = {}): Promise<Pagina<Lote>> {
  const corpo = await requisitar(CAMINHO_LOTES, {
    consulta: {
      ...parametrosDePagina(filtro),
      busca: filtro.busca,
      codFil: filtro.codFil,
      situacao: filtro.situacao,
    },
  });

  return analisar(paginaDeLotesSchema, corpo, CAMINHO_LOTES);
}

/** A mesma matriz do QR de produto (versão 2-H, 625 módulos), com `codLot`. */
export const qrDoLoteSchema = qrDoProdutoSchema.omit({ codPro: true }).extend({
  codLot: z.string(),
}) satisfies z.ZodType<Esquemas['QrLoteMatrizResponse']>;

export type QrDoLote = z.infer<typeof qrDoLoteSchema>;

/**
 * A matriz do QR de um `codLot` (`GET /lotes/qrcode?formato=matriz`).
 *
 * @throws {ErroApi} `LOTE_NAO_CABE_NO_QR` quando o texto não cabe na versão
 * 2-H; `RESPOSTA_INVALIDA` também quando a matriz é de OUTRO lote.
 */
export async function obterQrDoLote(codLot: string, sinal?: AbortSignal): Promise<QrDoLote> {
  const corpo = await requisitar(CAMINHO_QR, {
    consulta: { codLot, formato: 'matriz' },
    ...(sinal ? { sinal } : {}),
  });

  const qr = analisar(qrDoLoteSchema, corpo, CAMINHO_QR);

  if (qr.codLot !== codLot) {
    throw erroDeContrato(CAMINHO_QR);
  }

  return qr;
}

export const SITUACOES_DA_CARGA = ['NaoIniciada', 'EmAndamento', 'Concluida', 'Falhou'] as const;

const ETAPAS_DOS_ERROS = ['Categorias', 'Produtos', 'Saldos', 'Lotes'] as const;

/** Estado da carga de lotes (`GET /sincronizacao/lotes/carga`). */
export const cargaDeLotesSchema = z.object({
  status: z.enum(SITUACOES_DA_CARGA),
  emExecucao: z.boolean(),
  /** Saldos por lote gravados na execução atual. */
  lotesProcessados: z.number().int().nonnegative(),
  registrosComErro: z.number().int().nonnegative(),
  erros: z.array(z.object({ etapa: z.enum(ETAPAS_DOS_ERROS), mensagem: z.string() })),
  ultimoErro: z.string().nullable(),
  disparadaPor: z.string().nullable(),
  iniciadaEm: z.string().nullable(),
  concluidaEm: z.string().nullable(),
  /** A última varredura que leu todas as filiais até o fim. */
  varreduraCompletaEm: z.string().nullable(),
  /** Pelo menos uma varredura completa — condição do modo LOTE. */
  sincronizados: z.boolean(),
  duracaoMs: z.number().nonnegative().nullable(),
}) satisfies z.ZodType<Esquemas['CargaDeLotesResponse']>;

export type CargaDeLotes = z.infer<typeof cargaDeLotesSchema>;

const disparoDaCargaSchema = z.object({
  retomada: z.boolean(),
  carga: cargaDeLotesSchema,
}) satisfies z.ZodType<Esquemas['DisparoDaCargaDeLotesResponse']>;

export async function obterCargaDeLotes(): Promise<CargaDeLotes> {
  return analisar(cargaDeLotesSchema, await requisitar(CAMINHO_CARGA), CAMINHO_CARGA);
}

/**
 * Dispara (Administrador) a leitura dos lotes com saldo no ERP.
 *
 * @throws {ErroApi} 409 `CARGA_DE_LOTES_EM_ANDAMENTO`,
 * `CARGA_INICIAL_NAO_CONCLUIDA` ou `INTEGRACAO_ERP_INATIVA`.
 */
export async function dispararCargaDeLotes(): Promise<CargaDeLotes> {
  const corpo = await requisitar(CAMINHO_CARGA, { metodo: 'POST' });
  return analisar(disparoDaCargaSchema, corpo, CAMINHO_CARGA).carga;
}
